import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminPages.css';

const emptyForm = { title: '', subtitle: '', image: '', link: '', position: 'hero', isActive: true };

const Banners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { fetchBanners(); }, []);

  const fetchBanners = async () => {
    try {
      const res = await axios.get('/api/admin/banners');
      setBanners(res.data);
    } catch (err) {
      console.error('Error fetching banners:', err);
    } finally {
      setLoading(false);
    }
  };

  const openAddForm = () => { setForm(emptyForm); setEditingId(null); setError(''); setShowForm(true); };

  const openEditForm = (b) => {
    setForm({ title: b.title, subtitle: b.subtitle || '', image: b.image, link: b.link || '', position: b.position || 'hero', isActive: b.isActive });
    setEditingId(b._id); setError(''); setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditingId(null); setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.image) { setError('Title and image are required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`/api/admin/banners/${editingId}`, form);
      } else {
        await axios.post('/api/admin/banners', form);
      }
      closeForm(); fetchBanners();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving banner');
    } finally { setSaving(false); }
  };

  const toggleActive = async (id, current) => {
    try {
      await axios.put(`/api/admin/banners/${id}`, { isActive: !current });
      fetchBanners();
    } catch (err) { alert('Error updating banner'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try { await axios.delete(`/api/admin/banners/${id}`); fetchBanners(); } catch (err) { alert('Error deleting banner'); }
  };

  if (loading) return <div className="admin-loading">Loading...</div>;

  return (
    <div className="admin-page admin-page-wide">
      <div className="admin-page-header">
        <div>
          <h2 className="admin-page-title">Banners</h2>
          <p className="admin-page-desc">Manage promotional banners and hero images. ({banners.length} total)</p>
        </div>
        <button className="admin-btn admin-btn-primary" onClick={openAddForm}>+ Add Banner</button>
      </div>

      {showForm && (
        <div className="admin-modal-overlay" onClick={closeForm}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{editingId ? 'Edit Banner' : 'Add New Banner'}</h3>
              <button className="admin-modal-close" onClick={closeForm}>&times;</button>
            </div>
            <form onSubmit={handleSubmit} className="admin-material-form">
              {error && <div className="admin-form-error">{error}</div>}
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label>Title *</label>
                  <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
                </div>
                <div className="admin-form-group">
                  <label>Subtitle</label>
                  <input type="text" value={form.subtitle} onChange={e => setForm({ ...form, subtitle: e.target.value })} />
                </div>
                <div className="admin-form-group">
                  <label>Position</label>
                  <select value={form.position} onChange={e => setForm({ ...form, position: e.target.value })}>
                    <option value="hero">Hero</option>
                    <option value="category">Category Page</option>
                    <option value="promo">Promotional</option>
                  </select>
                </div>
                <div className="admin-form-group">
                  <label>Link URL</label>
                  <input type="text" value={form.link} onChange={e => setForm({ ...form, link: e.target.value })} placeholder="/browse" />
                </div>
              </div>
              <div className="admin-form-group admin-form-full">
                <label>Image URL *</label>
                <input type="text" value={form.image} onChange={e => setForm({ ...form, image: e.target.value })} required />
              </div>
              {form.image && <div className="admin-image-preview"><img src={form.image} alt="Preview" onError={e => { e.target.style.display = 'none'; }} /></div>}
              <div className="admin-form-actions">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={closeForm}>Cancel</button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>{saving ? 'Saving...' : editingId ? 'Update' : 'Add Banner'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="admin-page-card admin-table-card">
        <div className="admin-notifications-table">
          <table>
            <thead>
              <tr><th>Preview</th><th>Title</th><th>Position</th><th>Status</th><th>Created</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {banners.length === 0 ? (
                <tr><td colSpan="6" className="admin-no-data">No banners yet.</td></tr>
              ) : banners.map(b => (
                <tr key={b._id}>
                  <td><img src={b.image} alt={b.title} className="admin-material-thumb" style={{ width: '80px', height: '40px', objectFit: 'cover' }} /></td>
                  <td><strong>{b.title}</strong>{b.subtitle && <br />}{b.subtitle && <small style={{ color: '#64748b' }}>{b.subtitle}</small>}</td>
                  <td><span className="admin-category-badge">{b.position}</span></td>
                  <td>
                    <span className={`admin-status-badge ${b.isActive ? 'admin-status-admin' : 'admin-status-pending'}`}>
                      {b.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(b.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="admin-action-buttons">
                      <button className="admin-btn-icon admin-btn-edit" onClick={() => toggleActive(b._id, b.isActive)}>
                        {b.isActive ? 'Disable' : 'Enable'}
                      </button>
                      <button className="admin-btn-icon admin-btn-edit" onClick={() => openEditForm(b)}>Edit</button>
                      <button className="admin-btn-icon admin-btn-delete" onClick={() => handleDelete(b._id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Banners;
